import React, { useState, useRef, useEffect } from 'react'
import {
    Button,
    Col,
    Container,
    Form,
    FormFeedback,
    FormGroup,
    Input,
    InputGroup,
    InputGroupText,
    Label,
    Row,
    Toast,
    ToastBody,
    ToastHeader,
} from "reactstrap";
import { TitleBar } from "../../Components/Common/TitleBar/titleBar";
import { TripModal } from "../../Components/Common/TripModal/tripModal";
import { useDispatch, useSelector } from "react-redux";
import MaintenanceTableCategory from "./MaintenanceTable/MaintenanceTableCategory";
import InsuranceTableCategory from './InsuranceTable/InsuranceTableCategory';
import VehicleTypeTableCategory from './VehicleTypeTable/VehicleTypeTableCategory';
import { hideErrorMessage } from '../../slices/maintenanceCategory/reducer';
import { createMaintenanceCategory, getMaintenanceCategory, createInsuranceCategory, getInsuranceCategoryListApi, createVehicleCategory, getVehicleTypeCategory } from '../../slices/thunks';
import { setLoading } from '../../slices/maintenanceCategory/reducer';
import ResponsiveTabs from '../../Components/Common/ResponsiveTabs';
import { setLoading as setLoadingInsurance } from '../../slices/insuranceCategory/reducer';
import { setLoading as setLoadingVehicle } from '../../slices/vehicleTypeCategory/reducer';
import { Formik } from "formik";
import * as Yup from "yup";
import "./style.scss";

function Category() {
    const dispatch = useDispatch();
    const formikRef = useRef(null);
    const [activeTab, setActiveTab] = useState("1");
    const [searchText, setSearchText] = useState("");
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [isAdd, setIsAdd] = useState(false);
    const [addCategoryModal, setAddCategoryModal] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toasterType, setToasterType] = useState("");
    const loading = useSelector((state) => state.MaintenanceCategory?.loading);
    const loadingInsurance = useSelector((state) => state.InsuranceCategory?.loading);
    const loadingVehicle = useSelector((state) => state.VehicleTypeCategory?.loading);
    const tabs = [
        {
            id: "1",
            label: "Maintenance",
        },
        { id: "2", label: "Insurance" },
        { id: "3", label: "Vehicle Type" },
    ];
    const toggleAddCategoryModal = async () => {
        setAddCategoryModal(!addCategoryModal);
    };

    const validationSchemaForCategory = Yup.object().shape({
        name: Yup.string()
          .transform((value) => value.trim()) 
          .required("Category name is required")
          .test(
            "not-only-spaces",
            "Category name cannot be only spaces",
            (value) => !!value && value.trim().length > 0
          ),
      });

    const addInitialValues = {
        name: ""
    };

    const showToaster = (isSuccess, message) => {
        setShowToast(true);
        setToasterType(isSuccess ? "success" : "danger");
        setToastMessage(message);
        setTimeout(() => {
            setShowToast(false);
            if (!isSuccess) {
                dispatch(hideErrorMessage());
            }
        }, 3000);
    };

    const handleCategorySubmit = async (values) => {
        try {
            let response;
            switch (activeTab) {
                case "1":
                    response = await dispatch(createMaintenanceCategory(values));
                    break;
                case "2":
                    response = await dispatch(createInsuranceCategory(values));
                    break;
                case "3":
                    response = await dispatch(createVehicleCategory(values));
                    break;
                default:
                    throw new Error("Invalid category type");
            }
            const { success: isSuccess, message } = response;
            showToaster(isSuccess, message || "Operation successful");
        } catch (error) {
            showToaster(
                false,
                error?.message || "An error occurred while processing your request"
            );
        } finally {
            toggleAddCategoryModal();
            switch (activeTab) {
                case "1":
                    await dispatch(getMaintenanceCategory(currentPage, pageSize, searchText));
                    break;
                case "2":
                    await dispatch(getInsuranceCategoryListApi(currentPage, pageSize, searchText));
                    break;
                case "3":
                    await dispatch(getVehicleTypeCategory(currentPage, pageSize, searchText));
                    break;
                default:
                    break;
            }
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            try {
                dispatch(getMaintenanceCategory(currentPage, pageSize, searchText));
            } catch (error) { }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [dispatch, currentPage, pageSize, searchText]);
    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <div className="d-flex flex-column gap-4">
                        <TitleBar title={"Category Management"} />
                        <div
                            className="position-fixed top-0 end-0 p-3"
                            style={{ zIndex: 1055 }}
                        >
                            {showToast && (
                                <Toast isOpen={showToast}>
                                    <ToastHeader
                                        icon={"danger" === toasterType ? "danger" : "success"}
                                        toggle={() => setShowToast(false)}
                                    >
                                        {"danger" === toasterType ? "Error" : "Success"}
                                    </ToastHeader>
                                    <ToastBody>{toastMessage}</ToastBody>
                                </Toast>
                            )}
                        </div>

                        <div className="maintaince-table-header d-flex flex-column-reverse flex-sm-row justify-content-between align-items-end p-2 p-lg-0 gap-1 gap-lg-0">
                            <ResponsiveTabs
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                tabs={tabs}
                            />

                            <div className="d-flex maintaince-table-subheader">
                            <InputGroup
                                className="search-input"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        setCurrentPage(1);
                                        if (activeTab === "1") {
                                            dispatch(getMaintenanceCategory(1, pageSize, searchText));
                                        } else if (activeTab === "2") {
                                            dispatch(getInsuranceCategoryListApi(1, pageSize, searchText));
                                        } else if (activeTab === "3") {
                                            dispatch(getVehicleTypeCategory(1, pageSize, searchText));
                                        }
                                    }
                                }}
                            >
                                <InputGroupText>
                                    <i className="ri-search-line" />
                                </InputGroupText>
                                <Input type="text" placeholder="Search input text" />
                            </InputGroup>
                            
                            <Button
                                onClick={async () => {
                                    setIsAdd(true);
                                    toggleAddCategoryModal();
                                    if (activeTab === "1") {
                                        dispatch(setLoading({ type: "read", value: false }));
                                    } else if (activeTab === "2") {
                                        dispatch(setLoadingInsurance({ type: "read", value: false }));
                                    } else if (activeTab === "3") {
                                        dispatch(setLoadingVehicle({ type: "read", value: false }));
                                    }
                                }}
                                className="newMaintenanceBtn"
                            >
                                Add Category <i className="ri-add-line" />
                            </Button>
                            </div>
                        </div>

                        {activeTab === "1" ? <MaintenanceTableCategory /> : activeTab === "2" ? <InsuranceTableCategory /> : activeTab === "3" && <VehicleTypeTableCategory />}
                    </div>

                    <TripModal
                        isModalOpen={addCategoryModal}
                        toggleModal={toggleAddCategoryModal}
                        buttonText={isAdd && "Add"}
                        closeText={"Close without saving"}
                        title={"Category Management"}
                        tripText={isAdd && "Add new Category "}
                        showcloseIcon={true}
                        size="lg"
                        loading={
                            activeTab === "1"
                                ? loading?.create ?? false
                                : activeTab === "2"
                                    ? loadingInsurance?.create ?? false
                                    : loadingVehicle?.create ?? false
                        }
                        onSubmit={() => {
                            if (formikRef.current) {
                                formikRef.current.handleSubmit();
                            }
                        }}
                    >
                        <div className="px-3  gap-4">
                            <Formik
                                innerRef={formikRef}
                                enableReinitialize
                                initialValues={addInitialValues}
                                validationSchema={validationSchemaForCategory}
                                onSubmit={handleCategorySubmit}
                            >
                                {({
                                    values,
                                    errors,
                                    touched,
                                    handleChange,
                                    handleBlur,
                                    handleSubmit,
                                }) => (
                                    <Form onSubmit={handleSubmit}>
                                        <Row>
                                            <Col md={12}>
                                                <FormGroup>
                                                    <Label for="name" className="passwordLabel">
                                                        Name
                                                    </Label>
                                                    <Input
                                                        type="text"
                                                        name="name"
                                                        className="profileInput"
                                                        id="name"
                                                        placeholder="Enter Category Name"
                                                        value={values.name}
                                                        onBlur={handleBlur}
                                                        onChange={handleChange}
                                                    
                                                        invalid={
                                                            touched.name && errors.name
                                                                ? true
                                                                : false
                                                        }
                                                    />
                                                    {touched.name && errors.name ? (
                                                        <FormFeedback type="invalid">
                                                            {errors.name}
                                                        </FormFeedback>
                                                    ) : null}
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </TripModal>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default Category;
